import type { ProblemJSDependencies } from '../problemJSDependencies';

import { Direction } from './direction';
import { MathObject } from './MathObject';
import { buildMulti, Multi } from './Multi';

export class Line extends MathObject {
  problemJS: ProblemJSDependencies;

  lockViewportDim = false;
  multi: Multi;
  isDynamicSize = false; // Only true for ValueTable
  tableGetter?: any; // reference to ValueTable parent, if it exists
  lineHolder?: any; // unsure of use when porting from megafile to Line class
  normalHolder?: any; // unsure of use when porting from megafile to Line class
  maxVW?: number; // unsure of use when porting from megafile to Line class

  lineNumber?: any; // Text.ts expects this to possibly exist
  normalGetter?: any; // Text.ts expects this to possibly exist

  constructor(problemJS: ProblemJSDependencies, x = 0, y = 0) {
    super(x, y);

    this.t = 'line';
    this.gmmName = 'line';

    this.problemJS = problemJS;
    this.e = true;
    this.multi = buildMulti(problemJS, this);
    this.multi.gmmName = 'top-level multi'; // helpful when debugging
    this.children.push(this.multi);
    this.multi.parent = this;
  }

  balanceEmptyTexts(): void {
    this.multi.balanceEmptyTextsRecursive();
  }

  setLineOfChildren(): void {
    this.multi.setLine(this);
  }

  hasMultiLinesText(): boolean {
    return this.multi.hasMultiLinesText();
  }

  // Only non-zero is there is exactly one middle symbol
  pixelsToMiddleSymbol(whichMiddleSymbol?: number): number {
    if (!whichMiddleSymbol) {
      if (this.countMiddleSymbols() !== 1) {
        return 0;
      }

      whichMiddleSymbol = 1;
    }

    if (!Number.isInteger(whichMiddleSymbol) || whichMiddleSymbol <= 0) {
      throw new Error('whichMiddleSymbol must be a positive whole number');
    }

    let foundCount = 0;

    const multC = this.multi.children as MathObject[];

    for (let a = 0; a < multC.length; a++) {
      const child = multC[a];

      const childPixelsToMiddle = child.pixelsToMiddleSymbol();

      if (childPixelsToMiddle !== 0) {
        foundCount++;

        if (foundCount === whichMiddleSymbol) {
          return child.viewportX + childPixelsToMiddle;
        }
      }
    }

    return 0;
  }

  notifySizeChanged(): void {
    if (this.isDynamicSize) {
      const row = this.getRow() as MathObject;

      if (row !== undefined) row.buildSizeRecursive();
    }
  }

  buildSize(): void {
    if (!this.lockViewportDim) {
      if (this.isDynamicSize || !this.maxVW) {
        this.setAllDim(this.multi.viewportW, this.multi.viewportH);
      } else {
        this.setAllDim(
          Math.min(this.multi.viewportW, this.maxVW),
          this.multi.viewportH
        );
      }
    } else {
      this.setAllDim(this.multi.viewportW, this.multi.viewportH);
      // I'll assume that locking the dim means you want
      // vertical centering.
      const dy =
        (this.viewportH - 2 * this.viewportMargin) / 2 - this.height / 2;

      this.multi.viewportY = dy;
    }
  }

  setFont(f: string): void {
    this.multi.setFont(f);
  }

  buildSizeRecursive(): void {
    this.multi.buildSizeRecursive();
    this.buildSize();
  }

  childSizeChanged(): void {
    this.buildSize();

    if (this.lineHolder) {
      this.lineHolder.lineSizeChanged();
    } else if (this.normalHolder) {
      this.normalHolder.repositionLines();
    }
  }

  // Overrides BasicObject.add to ensure indexed addition
  // gets passed down to the multi
  add(addMe: MathObject, index?: number) {
    this.multi.add(addMe, index);
    addMe.parent = this.multi;
  }

  grabFocus(dir: Direction): void {
    this.multi.grabFocus(dir);
  }

  // tell holder so it can resize to show submit and icons
  gainedFocus(): void {
    const currentAttemptGetter = this.problemJS.getCurrentAttemptGetter();

    if (currentAttemptGetter && currentAttemptGetter.gmmName == 'gridGetter') {
      currentAttemptGetter.g.setSelected(false);
    }

    if (this.normalHolder) {
      this.normalHolder.childGainedFocus();
    } else if (this.tableGetter) {
      this.tableGetter.childGainedFocus();
    }
  }

  toXML(): string {
    return this.multi.toXML();
  }

  swapString(old: string, replacer: string): void {
    this.multi.swapString(old, replacer);
  }

  clear(): void {
    this.multi = buildMulti(this.problemJS, this);
    this.children = [];
    this.children.push(this.multi);
    this.multi.parent = this;
    this.balanceEmptyTexts();
    this.buildSizeRecursive();
    this.problemJS.storeCurrentLineXML();
  }

  setEditable(b: boolean): void {
    this.multi.setEditable(b);
    this.e = b;
  }

  isEditable(): boolean {
    return !!this.e;
  }

  isFocused(): boolean {
    return !!(
      this.problemJS.getFocused() && this.problemJS.getFocused()?.line === this
    );
  }
}
